import { useState, useEffect, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Instagram,
  Users,
  Heart,
  MessageCircle,
  Eye,
  Share2,
  Bookmark,
  TrendingUp,
  Image,
  Video,
  LayoutGrid,
  ExternalLink,
  RefreshCw,
  Upload,
  ChevronLeft,
  ChevronRight,
  BarChart2,
  UserCheck,
  FileText,
  Link2,
  Link2Off,
  AlertCircle,
} from "lucide-react";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatNumber(n: number) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return String(n);
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
}

function mediaTypeIcon(type: string) {
  if (type === "VIDEO") return <Video className="w-3 h-3" />;
  if (type === "CAROUSEL_ALBUM") return <LayoutGrid className="w-3 h-3" />;
  return <Image className="w-3 h-3" />;
}

// ─── Tela de Conexão ──────────────────────────────────────────────────────────
type ConnectableSalon = { id: number; name: string };

function ConnectScreen({ onConnected, salons, selectedSalonId, onSalonChange }: {
  onConnected: () => void;
  salons: ConnectableSalon[];
  selectedSalonId: number | null;
  onSalonChange: (salonId: number) => void;
}) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [showManualToken, setShowManualToken] = useState(false);
  const [manualToken, setManualToken] = useState("");
  const [isSavingManualToken, setIsSavingManualToken] = useState(false);

  const handleConnect = () => {
    if (!selectedSalonId) {
      toast.error("Selecione a unidade que será conectada ao Instagram.");
      return;
    }
    setIsConnecting(true);
    // Redireciona para o fluxo OAuth do Meta
    const origin = window.location.origin;
    window.location.href = `/api/instagram/oauth/start?origin=${encodeURIComponent(origin)}&salonId=${selectedSalonId}`;
  };

  const handleManualToken = async () => {
    if (!selectedSalonId) {
      toast.error("Selecione a unidade que será conectada ao Instagram.");
      return;
    }
    const accessToken = manualToken.trim();
    if (accessToken.length < 20) {
      toast.error("Informe o token gerado no painel Meta.");
      return;
    }

    setIsSavingManualToken(true);
    try {
      const response = await fetch("/api/instagram/oauth/connect-token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessToken, salonId: selectedSalonId }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error ?? "Não foi possível validar o token do Instagram");

      toast.success("Instagram conectado com sucesso.", {
        description: result.messagesSubscribed
          ? "A conta também foi inscrita para receber novos Directs."
          : "A conta foi conectada; a inscrição de mensagens será verificada antes da ativação.",
      });
      setShowManualToken(false);
      onConnected();
    } catch (error: any) {
      toast.error("Token não conectado", { description: error.message ?? "Verifique o token e a conta profissional." });
    } finally {
      setManualToken("");
      setIsSavingManualToken(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center space-y-6">
      <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center shadow-lg">
        <Instagram className="w-10 h-10 text-white" />
      </div>
      <div className="space-y-2">
        <h2 className="text-2xl font-bold">Conectar Instagram</h2>
        <p className="text-muted-foreground max-w-sm">
          Conecte a conta Instagram Business ou Creator do seu salão para gerenciar posts, ver métricas e publicar conteúdo diretamente pelo sistema.
        </p>
      </div>
      <div className="bg-muted/50 rounded-xl p-4 text-left space-y-2 max-w-sm w-full">
        <p className="text-sm font-medium">O que você poderá fazer:</p>
        <ul className="text-sm text-muted-foreground space-y-1">
          <li className="flex items-center gap-2"><span className="text-green-500">✓</span> Ver perfil e estatísticas da conta</li>
          <li className="flex items-center gap-2"><span className="text-green-500">✓</span> Listar e navegar pelos posts</li>
          <li className="flex items-center gap-2"><span className="text-green-500">✓</span> Ver métricas detalhadas por post</li>
          <li className="flex items-center gap-2"><span className="text-green-500">✓</span> Publicar imagens, vídeos, Reels e Stories</li>
        </ul>
      </div>
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800 max-w-sm w-full text-left">
        <p className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>É necessário ter uma conta <strong>Instagram Business ou Creator</strong> vinculada a uma Página do Facebook.</span>
        </p>
      </div>
      {salons.length > 1 && (
        <div className="max-w-sm w-full space-y-2 text-left">
          <label className="text-sm font-medium">Unidade para conectar</label>
          <Select value={selectedSalonId ? String(selectedSalonId) : undefined} onValueChange={(value) => onSalonChange(Number(value))}>
            <SelectTrigger><SelectValue placeholder="Selecione a unidade" /></SelectTrigger>
            <SelectContent>
              {salons.map((salon) => <SelectItem key={salon.id} value={String(salon.id)}>{salon.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <Button
        onClick={handleConnect}
        disabled={isConnecting}
        className="gap-2 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white px-8"
        size="lg"
      >
        {isConnecting ? (
          <><RefreshCw className="w-4 h-4 animate-spin" /> Redirecionando...</>
        ) : (
          <><Link2 className="w-4 h-4" /> Conectar com o Instagram</>
        )}
      </Button>
      <div className="w-full max-w-sm border-t pt-4 space-y-3 text-left">
        <Button
          type="button"
          variant="outline"
          className="w-full gap-2"
          onClick={() => setShowManualToken((visible) => !visible)}
        >
          <Link2 className="w-4 h-4" />
          {showManualToken ? "Cancelar token manual" : "Conectar com token do painel Meta"}
        </Button>
        {showManualToken && (
          <div className="space-y-3 rounded-xl border border-pink-200 bg-pink-50/40 p-4">
            <div>
              <p className="text-sm font-medium">Token de acesso da conta profissional</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Cole o token gerado em <strong>API do Instagram → Gerar tokens de acesso</strong>. Ele é enviado diretamente ao servidor, validado sem enviar Directs e apagado deste formulário após o uso.
              </p>
            </div>
            <Input
              type="password"
              autoComplete="off"
              spellCheck={false}
              placeholder="Cole o token do Instagram"
              value={manualToken}
              onChange={(event) => setManualToken(event.target.value)}
            />
            <Button
              type="button"
              className="w-full gap-2 bg-pink-600 hover:bg-pink-700"
              disabled={isSavingManualToken || manualToken.trim().length < 20 || !selectedSalonId}
              onClick={handleManualToken}
            >
              {isSavingManualToken ? <><RefreshCw className="w-4 h-4 animate-spin" /> Validando token...</> : <><Instagram className="w-4 h-4" /> Validar e conectar</>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab: Perfil ──────────────────────────────────────────────────────────────
function ProfileTab({ onDisconnect, salonId }: { onDisconnect: () => void; salonId: number | null }) {
  const profileInput = useMemo(() => salonId ? { salonId } : undefined, [salonId]);
  const { data, isLoading, refetch } = trpc.instagram.getAccountInfo.useQuery(profileInput);
  const [disconnecting, setDisconnecting] = useState(false);

  const handleDisconnect = async () => {
    if (!confirm("Tem certeza que deseja desconectar o Instagram? Você poderá reconectar a qualquer momento.")) return;
    setDisconnecting(true);
    try {
      await fetch("/api/instagram/oauth/disconnect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ salonId }),
      });
      toast.success("Instagram desconectado com sucesso.");
      onDisconnect();
    } catch {
      toast.error("Erro ao desconectar o Instagram.");
    } finally {
      setDisconnecting(false);
    }
  };

  if (isLoading) return (
    <div className="space-y-4">
      <Skeleton className="h-32 w-32 rounded-full mx-auto" />
      <Skeleton className="h-6 w-48 mx-auto" />
      <Skeleton className="h-4 w-64 mx-auto" />
    </div>
  );

  if (!data) return <p className="text-muted-foreground text-center py-8">Não foi possível carregar o perfil.</p>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center gap-4 text-center">
        {data.profile_picture_url && (
          <img
            src={data.profile_picture_url}
            alt={data.username}
            className="w-24 h-24 rounded-full border-4 border-pink-200 object-cover"
          />
        )}
        <div>
          <h2 className="text-xl font-bold">@{data.username}</h2>
          {data.name && <p className="text-muted-foreground text-sm">{data.name}</p>}
        </div>
        {data.biography && (
          <p className="text-sm max-w-md whitespace-pre-line text-muted-foreground">{data.biography}</p>
        )}
        {data.website && (
          <a href={data.website} target="_blank" rel="noopener noreferrer" className="text-pink-500 text-sm flex items-center gap-1 hover:underline">
            <ExternalLink className="w-3 h-3" /> {data.website}
          </a>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-pink-600">{formatNumber(data.followers_count ?? 0)}</p>
            <p className="text-xs text-muted-foreground mt-1 flex items-center justify-center gap-1"><Users className="w-3 h-3" /> Seguidores</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-purple-600">{formatNumber(data.follows_count ?? 0)}</p>
            <p className="text-xs text-muted-foreground mt-1 flex items-center justify-center gap-1"><UserCheck className="w-3 h-3" /> Seguindo</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-orange-600">{formatNumber(data.media_count ?? 0)}</p>
            <p className="text-xs text-muted-foreground mt-1 flex items-center justify-center gap-1"><FileText className="w-3 h-3" /> Posts</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-between items-center pt-2 border-t">
        <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-2">
          <RefreshCw className="w-4 h-4" /> Atualizar
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleDisconnect}
          disabled={disconnecting}
          className="gap-2 text-destructive border-destructive/30 hover:bg-destructive/5"
        >
          <Link2Off className="w-4 h-4" />
          {disconnecting ? "Desconectando..." : "Desconectar Instagram"}
        </Button>
      </div>
    </div>
  );
}

// ─── Tab: Posts ───────────────────────────────────────────────────────────────
function PostsTab({ onSelectPost, salonId }: { onSelectPost: (id: string) => void; salonId: number | null }) {
  const [after, setAfter] = useState<string | undefined>();
  const [history, setHistory] = useState<string[]>([]);

  const postListInput = useMemo(() => ({ limit: 12, after, ...(salonId ? { salonId } : {}) }), [after, salonId]);
  const { data, isLoading } = trpc.instagram.getPostList.useQuery(postListInput);

  const goNext = () => {
    if (data?.paging?.cursors?.after) {
      setHistory((h) => [...h, after ?? ""]);
      setAfter(data.paging.cursors.after);
    }
  };

  const goPrev = () => {
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setAfter(prev === "" ? undefined : prev);
  };

  if (isLoading) return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {Array.from({ length: 9 }).map((_, i) => (
        <Skeleton key={i} className="aspect-square rounded-lg" />
      ))}
    </div>
  );

  const posts = data?.posts ?? [];

  return (
    <div className="space-y-4">
      {posts.length === 0 && (
        <p className="text-center text-muted-foreground py-8">Nenhum post encontrado.</p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {posts.map((post: any) => (
          <div
            key={post.id}
            className="relative group cursor-pointer rounded-lg overflow-hidden border hover:border-pink-400 transition-colors"
            onClick={() => onSelectPost(post.id)}
          >
            {post.media_url || post.thumbnail_url ? (
              <img
                src={post.thumbnail_url ?? post.media_url}
                alt={post.caption?.slice(0, 40) ?? "Post"}
                className="w-full aspect-square object-cover"
              />
            ) : (
              <div className="w-full aspect-square bg-muted flex items-center justify-center">
                <Image className="w-8 h-8 text-muted-foreground" />
              </div>
            )}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2">
              <div className="flex items-center gap-2 text-white text-xs">
                <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {post.like_count ?? 0}</span>
                <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {post.comments_count ?? 0}</span>
              </div>
            </div>
            <div className="absolute top-2 right-2">
              <Badge variant="secondary" className="text-xs px-1 py-0 gap-1 flex items-center">
                {mediaTypeIcon(post.media_type)}
              </Badge>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={goPrev} disabled={history.length === 0} className="gap-1">
          <ChevronLeft className="w-4 h-4" /> Anterior
        </Button>
        <Button variant="outline" size="sm" onClick={goNext} disabled={!data?.paging?.cursors?.after} className="gap-1">
          Próxima <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// ─── Tab: Métricas ────────────────────────────────────────────────────────────
function MetricsTab({ selectedPostId, onBack, salonId }: { selectedPostId: string | null; onBack: () => void; salonId: number | null }) {
  const insightsInput = useMemo(() => ({ postId: selectedPostId!, ...(salonId ? { salonId } : {}) }), [selectedPostId, salonId]);
  const { data, isLoading } = trpc.instagram.getPostInsights.useQuery(
    insightsInput,
    { enabled: !!selectedPostId }
  );

  if (!selectedPostId) return (
    <div className="text-center py-12 text-muted-foreground">
      <BarChart2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
      <p>Selecione um post na aba <strong>Posts</strong> para ver as métricas.</p>
    </div>
  );

  if (isLoading) return (
    <div className="space-y-3">
      <Skeleton className="h-48 w-full rounded-lg" />
      <div className="grid grid-cols-2 gap-3">
        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)}
      </div>
    </div>
  );

  if (!data) return <p className="text-center text-muted-foreground py-8">Não foi possível carregar as métricas.</p>;

  const { post, metrics } = data;

  const metricCards = [
    { label: "Alcance", value: metrics.reach, icon: <TrendingUp className="w-4 h-4 text-blue-500" />, color: "text-blue-600" },
    { label: "Impressões", value: metrics.impressions, icon: <Eye className="w-4 h-4 text-purple-500" />, color: "text-purple-600" },
    { label: "Curtidas", value: metrics.likes, icon: <Heart className="w-4 h-4 text-red-500" />, color: "text-red-600" },
    { label: "Comentários", value: metrics.comments, icon: <MessageCircle className="w-4 h-4 text-green-500" />, color: "text-green-600" },
    { label: "Salvamentos", value: metrics.saved, icon: <Bookmark className="w-4 h-4 text-yellow-500" />, color: "text-yellow-600" },
    { label: "Compartilhamentos", value: metrics.shares, icon: <Share2 className="w-4 h-4 text-orange-500" />, color: "text-orange-600" },
  ];

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={onBack} className="gap-1 -ml-2">
        <ChevronLeft className="w-4 h-4" /> Voltar aos posts
      </Button>

      <div className="flex gap-3 items-start">
        {(post.thumbnail_url ?? post.media_url) && (
          <img
            src={post.thumbnail_url ?? post.media_url}
            alt="Post"
            className="w-20 h-20 rounded-lg object-cover flex-shrink-0 border"
          />
        )}
        <div className="flex-1 min-w-0">
          {post.caption && <p className="text-sm line-clamp-3 text-muted-foreground">{post.caption}</p>}
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="text-xs gap-1 flex items-center">
              {mediaTypeIcon(post.media_type)} {post.media_type}
            </Badge>
            <span className="text-xs text-muted-foreground">{formatDate(post.timestamp)}</span>
          </div>
          {post.permalink && (
            <a href={post.permalink} target="_blank" rel="noopener noreferrer" className="text-xs text-pink-500 flex items-center gap-1 mt-1 hover:underline">
              <ExternalLink className="w-3 h-3" /> Ver no Instagram
            </a>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {metricCards.map((m) => (
          <Card key={m.label}>
            <CardContent className="pt-4 text-center">
              <div className="flex justify-center mb-1">{m.icon}</div>
              <p className={`text-2xl font-bold ${m.color}`}>{formatNumber(m.value)}</p>
              <p className="text-xs text-muted-foreground mt-1">{m.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─── Tab: Publicar ────────────────────────────────────────────────────────────
function PublishTab({ igUsername, salonId }: { igUsername?: string; salonId: number | null }) {
  const [form, setForm] = useState({
    type: "IMAGE" as "IMAGE" | "VIDEO" | "REEL" | "STORY" | "CAROUSEL",
    mediaUrls: [""],
    caption: "",
    shareToFeed: true,
    coverUrl: "",
  });
  const [confirmed, setConfirmed] = useState(false);

  const createPost = trpc.instagram.createPost.useMutation({
    onSuccess: () => {
      toast.success("✅ Publicado com sucesso!", { description: "Seu post foi publicado no Instagram." });
      setForm({ type: "IMAGE", mediaUrls: [""], caption: "", shareToFeed: true, coverUrl: "" });
      setConfirmed(false);
    },
    onError: (err) => {
      toast.error("Erro ao publicar", { description: err.message });
    },
  });

  const addUrl = () => setForm((f) => ({ ...f, mediaUrls: [...f.mediaUrls, ""] }));
  const removeUrl = (i: number) => setForm((f) => ({ ...f, mediaUrls: f.mediaUrls.filter((_, idx) => idx !== i) }));
  const updateUrl = (i: number, val: string) =>
    setForm((f) => ({ ...f, mediaUrls: f.mediaUrls.map((u, idx) => (idx === i ? val : u)) }));

  const validUrls = form.mediaUrls.filter((u) => u.trim().length > 0);
  const canPublish = validUrls.length > 0;

  const handlePublish = () => {
    if (!confirmed) { setConfirmed(true); return; }
    createPost.mutate({
      type: form.type,
      mediaUrls: validUrls,
      caption: form.caption || undefined,
      shareToFeed: form.shareToFeed,
      coverUrl: form.coverUrl || undefined,
      salonId: salonId ?? undefined,
    });
    setConfirmed(false);
  };

  return (
    <div className="space-y-5 max-w-lg">
      <div className="space-y-2">
        <label className="text-sm font-medium">Tipo de publicação</label>
        <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v as any }))}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="IMAGE">📷 Imagem</SelectItem>
            <SelectItem value="VIDEO">🎬 Vídeo</SelectItem>
            <SelectItem value="REEL">🎞️ Reel</SelectItem>
            <SelectItem value="STORY">📱 Story</SelectItem>
            <SelectItem value="CAROUSEL">🖼️ Carrossel (até 10 mídias)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">
          URL{form.type === "CAROUSEL" ? "s" : ""} da mídia
          <span className="text-muted-foreground font-normal ml-1 text-xs">(link público da imagem/vídeo)</span>
        </label>
        {form.mediaUrls.map((url, i) => (
          <div key={i} className="flex gap-2">
            <Input
              placeholder="https://exemplo.com/imagem.jpg"
              value={url}
              onChange={(e) => updateUrl(i, e.target.value)}
            />
            {form.type === "CAROUSEL" && form.mediaUrls.length > 1 && (
              <Button variant="ghost" size="icon" onClick={() => removeUrl(i)} className="text-destructive flex-shrink-0">×</Button>
            )}
          </div>
        ))}
        {form.type === "CAROUSEL" && form.mediaUrls.length < 10 && (
          <Button variant="outline" size="sm" onClick={addUrl} className="gap-1">
            <Upload className="w-3 h-3" /> Adicionar mídia
          </Button>
        )}
      </div>

      {form.type !== "STORY" && (
        <div className="space-y-2">
          <label className="text-sm font-medium">Legenda</label>
          <Textarea
            placeholder="Escreva a legenda do post... (máx. 2200 caracteres)"
            value={form.caption}
            onChange={(e) => setForm((f) => ({ ...f, caption: e.target.value }))}
            maxLength={2200}
            rows={4}
          />
          <p className="text-xs text-muted-foreground text-right">{form.caption.length}/2200</p>
        </div>
      )}

      {form.type === "REEL" && (
        <div className="space-y-2">
          <label className="text-sm font-medium">URL da capa do Reel (opcional)</label>
          <Input
            placeholder="https://exemplo.com/capa.jpg"
            value={form.coverUrl}
            onChange={(e) => setForm((f) => ({ ...f, coverUrl: e.target.value }))}
          />
        </div>
      )}

      {confirmed && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
          ⚠️ <strong>Confirmar publicação?</strong> Este post será publicado imediatamente no Instagram
          {igUsername ? <strong> @{igUsername}</strong> : ""}.
        </div>
      )}

      <Button
        onClick={handlePublish}
        disabled={!canPublish || createPost.isPending}
        className={`w-full gap-2 ${confirmed ? "bg-green-600 hover:bg-green-700" : "bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"}`}
      >
        {createPost.isPending ? (
          <><RefreshCw className="w-4 h-4 animate-spin" /> Publicando...</>
        ) : confirmed ? (
          "✅ Confirmar e Publicar"
        ) : (
          <><Instagram className="w-4 h-4" /> Publicar no Instagram</>
        )}
      </Button>
      {confirmed && (
        <Button variant="ghost" size="sm" className="w-full" onClick={() => setConfirmed(false)}>
          Cancelar
        </Button>
      )}
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function AdminInstagram() {
  const [activeTab, setActiveTab] = useState<"perfil" | "posts" | "metricas" | "publicar">("perfil");
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [selectedSalonId, setSelectedSalonId] = useState<number | null>(null);
  const { data: connectableSalons = [] } = trpc.instagram.listConnectableSalons.useQuery();
  // Em administração multiunidade, a escolha é obrigatória. Nunca assumir a
  // primeira unidade evita vincular uma conta profissional ao salão errado.
  const activeSalonId = selectedSalonId ?? (connectableSalons.length === 1 ? connectableSalons[0]?.id ?? null : null);
  const connectionInput = useMemo(() => activeSalonId ? { salonId: activeSalonId } : undefined, [activeSalonId]);

  // Verificar status da conexão
  const { data: connStatus, isLoading: connLoading, refetch: refetchConn } = trpc.instagram.getConnectionStatus.useQuery(connectionInput, { enabled: activeSalonId !== null });

  // Verificar parâmetros de URL (retorno do OAuth)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "1") {
      toast.success("Instagram conectado com sucesso!");
      refetchConn();
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("error")) {
      const err = params.get("error")!;
      if (err === "no_ig_business_account") {
        toast.error("Nenhuma conta Instagram Business encontrada", {
          description: "Certifique-se de que sua conta Instagram está vinculada a uma Página do Facebook.",
        });
      } else {
        toast.error("Erro ao conectar Instagram", { description: err });
      }
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const tabs = [
    { id: "perfil", label: "Perfil", icon: <Instagram className="w-4 h-4" /> },
    { id: "posts", label: "Posts", icon: <LayoutGrid className="w-4 h-4" /> },
    { id: "metricas", label: "Métricas", icon: <BarChart2 className="w-4 h-4" /> },
    { id: "publicar", label: "Publicar", icon: <Upload className="w-4 h-4" /> },
  ] as const;

  const handleSelectPost = (id: string) => {
    setSelectedPostId(id);
    setActiveTab("metricas");
  };

  if (connLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[300px]">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
          <Instagram className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold">Instagram</h1>
          <p className="text-sm text-muted-foreground">
            {connStatus?.connected
              ? <span className="flex items-center gap-1 text-green-600"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> @{connStatus.igUsername} conectado</span>
              : "Não conectado"
            }
          </p>
        </div>
      </div>

      {/* Se não conectado, mostrar tela de conexão */}
      {!connStatus?.connected ? (
        <Card>
          <CardContent className="p-0">
            <ConnectScreen
              onConnected={() => refetchConn()}
              salons={connectableSalons}
              selectedSalonId={activeSalonId}
              onSalonChange={setSelectedSalonId}
            />
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Tabs — select no mobile, botões no desktop */}
          <div className="sm:hidden">
            <Select value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {tabs.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    <span className="flex items-center gap-2">{t.icon} {t.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="hidden sm:flex gap-2 border-b pb-2">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                  activeTab === t.id
                    ? "bg-gradient-to-r from-pink-500 to-purple-600 text-white"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <Card>
            <CardContent className="pt-5">
              {activeTab === "perfil" && <ProfileTab salonId={activeSalonId} onDisconnect={() => refetchConn()} />}
              {activeTab === "posts" && <PostsTab salonId={activeSalonId} onSelectPost={handleSelectPost} />}
              {activeTab === "metricas" && (
                <MetricsTab
                  selectedPostId={selectedPostId}
                  onBack={() => setActiveTab("posts")}
                  salonId={activeSalonId}
                />
              )}
              {activeTab === "publicar" && <PublishTab salonId={activeSalonId} igUsername={connStatus?.igUsername ?? undefined} />}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
